import { z } from "zod";

/**
 * `DEALER-V2-B1B-CONTRACT` (`P1-DOS-077`): **the dealer's pièces door, the answer.** One definition for the door (a later PR; the path is for the architect to settle) and for the dealer client. Contract only: no door
 * answers it yet.
 *
 * **The dealer's own concession only.** The door reads a dossier of the caller's organisation (`seller_organisation_id`) and answers 404 for another concession's dossier or an absent one. Every employee of the
 * concession may call it (D-1031-18). It serves pièce FACTS and never identity, income or a document: the read journal of A50 belongs to the doors that serve identity or income.
 *
 * **« ready » is the complement of `manquants`** (`PieceService.manquants`, `SHORT_MULTI_FILE_SQL`): a pièce is `expected` when its definition is not retired and it is not « sans objet »; it is `ready` when its
 * status is `recue` and, for a multi-file pièce, its live declared months reach `expectedFiles`. A conditional pièce has a row, hence counts as expected, only once its condition has been met. So
 * `ready + manquants = expected`, and the dealer's bar can never disagree with the send guard. `summary` serves the two numbers so the browser never counts.
 *
 * **The months (the architect's ruling of 1 November).** Always served: `coveredMonths`, `expectedFiles`, `missingCount`. Served ONLY once a pièces reading exists: `readMonths` (`monthsReadFor`) and `missingMonths`,
 * computed by the SAME function the coverage gate uses (`coverageShortfalls`, `coverage-gate.ts`), so the dealer and the chargée see the same missing months; both are `null` before a reading. No month is derived
 * from the clock alone: that would imply a recency check the gate does not make. Declared month names are not served.
 *
 * **AMENDED after a reading (`P1-DOS-080`, the A-H architect's ruling of 1 November, ONE TRUTH AFTER THE READING; the text above stays as the record of the earlier ruling).** Once a reading speaks for a pièce
 * (it read the pièce and found at least one month, or found it short), `coveredMonths`, `expectedFiles` and `missingCount` are `null` and `monthsSentence` carries only the reading's sentence: the months found missing,
 * or the complete line « Mois lu par Hakili : {mois}. » / « Mois lus par Hakili : {mois}. » (the composer's own list form, the year on every month). Before a reading, the declared count is unchanged. A pièce a
 * reading did not read keeps the declared count. `summary.missingSentence` is the guard's own count sentence (`piecesManquantes`), `null` exactly when no pièce is missing.
 *
 * **The reading state** is the pièce-level `lue` / `illisible` / `en_lecture` of `piece-state.ts`, `null` when there is none, beside the chargée's `rejectionReason` where one exists. The capture-time, per-page
 * « lisible / à refaire » verdict is a separate, booked item.
 */
export const DEALER_DOCUMENT_STATUSES = ["manquante", "recue", "rejetee", "sansObjet"] as const;
export const DEALER_DOCUMENT_READINGS = ["lue", "illisible", "en_lecture"] as const;

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/u;
const DAY = /^\d{4}-\d{2}-\d{2}$/u;

const buildDealerDocuments = (object: typeof z.strictObject) => {
  const condition = object({
    code: z.string().min(1),
    /** The approved sentence that words the condition; `null` for a code with no sentence. */
    label: z.string().nullable(),
  });
  /** The vehicle's own pièces (carte grise, visite technique, proforma): where it came from and its dates. */
  const vehicleFact = object({
    /** `vehicule`: filed on the vehicle; `emise`: issued by the dealership; `concessionnaire`: deposited by the dealer. */
    provenance: z.enum(["vehicule", "emise", "concessionnaire"]),
    /** The day it was recorded or issued, `AAAA-MM-JJ`. */
    recordedOn: z.string().regex(DAY).nullable(),
    /** The validity date (a visite technique, a proforma), `AAAA-MM-JJ`; `null` where the pièce has none. */
    validUntil: z.string().regex(DAY).nullable(),
  });
  const piece = object({
    code: z.string().min(1),
    label: z.string().min(1),
    shortLabel: z.string().min(1),
    suppliedBy: z.enum(["client", "vendeur"]),
    status: z.enum(DEALER_DOCUMENT_STATUSES),
    /** The one server definition (see the file's comment): counted in `summary.ready`. */
    ready: z.boolean(),
    helpText: z.string().nullable(),
    /** The condition a conditional pièce is asked under; `null` for a pièce asked of every file. */
    condition: condition.nullable(),
    /** « Demandé par la banque »: a bank's completion request stands on this pièce. */
    bankRequested: z.boolean(),
    /** The instant it was received, ISO; `null` while it is not. */
    receivedAt: z.string().nullable(),
    multiFile: z.boolean(),
    /** How many months a multi-file pièce asks for; `null` for a pièce that asks for one document. */
    expectedFiles: z.number().int().min(2).nullable(),
    /** The months the LIVE files declare; `null` for a single-file pièce. */
    coveredMonths: z.number().int().min(0).nullable(),
    /** `expectedFiles` less `coveredMonths`, never negative; `null` for a single-file pièce. */
    missingCount: z.number().int().min(0).nullable(),
    /** `AAAA-MM`, oldest first: the months the latest successful pièces reading found; `null` before a reading, for a pièce not read, and for a single-file pièce. */
    readMonths: z.array(z.string().regex(MONTH)).nullable(),
    /** `AAAA-MM`, oldest first, by the coverage gate's own function; `null` exactly when `readMonths` is `null`. An empty list is a pièce read and complete. */
    missingMonths: z.array(z.string().regex(MONTH)).nullable(),
    /**
     * The months line, served (`B1b words`, the A-H architect's approval of 1 November, one source for all platforms), `null` for a single-file pièce. BEFORE a reading, or once a reading finds the pièce
     * complete: `coveredMonthsOfExpected`, « 3 mois reçus sur 3 ». AFTER a reading that finds months missing: the approved coverage words, « Les bulletins de salaire envoyés ne couvrent pas août 2026 : ajoutez le
     * document de ce mois. ». The browser composes none of it.
     */
    monthsSentence: z.string().min(1).nullable(),
    /** The pièce-level reading state; `null` when none. */
    reading: z.enum(DEALER_DOCUMENT_READINGS).nullable(),
    /** The reading line, served: `lue` « Lue par Hakili », `illisible` « Illisible : à refaire », `en_lecture` « En cours de lecture »; `null` exactly when `reading` is `null`. */
    readingSentence: z.string().min(1).nullable(),
    /** The chargée's rejection reason where the pièce was rejected; `null` otherwise. */
    rejectionReason: z.string().nullable(),
    /** Present for the vehicle's own pièces; `null` for every other. */
    vehicle: vehicleFact.nullable(),
  });
  const summary = object({
    ready: z.number().int().min(0),
    expected: z.number().int().min(0),
    /** `B1b-MISSING-SENTENCE`: the guard's own count sentence, « 1 pièce manquante » / « N pièces manquantes » (`piecesManquantes`, WF-CON-08); `null` exactly when no pièce is missing. No labels. */
    missingSentence: z.string().min(1).nullable(),
  });
  return object({ summary, pieces: z.array(piece) });
};

/** The dealer's pièces door, the 200 answer. Strict: an unknown key is a mismatch. */
export const dealerDocumentsAnswerSchema = buildDealerDocuments(z.strictObject);
/** The dealer client's reading of the same answer: unknown keys tolerated at every level. */
export const dealerDocumentsLenientReadSchema = buildDealerDocuments(z.looseObject);

export type DealerDocumentsAnswer = z.infer<typeof dealerDocumentsAnswerSchema>;
export type DealerDocumentView = DealerDocumentsAnswer["pieces"][number];
